import React, { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { FaStar } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
 
const API_BASE_URL = process.env.REACT_APP_API_URL.replace(/\/$/, '');
const axiosConfig = { headers: { "Content-Type": "application/json" } };
 
const CreateFeedback = () => {
    const { register, handleSubmit, control, formState: { errors }, reset, setValue } = useForm();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const encodedData = queryParams.get("data");
    const decodedData = encodedData ? JSON.parse(atob(encodedData)) : null;
    const [feedbackLinkId, setFeedbackLinkId] = useState(null);
    const [isValid, setIsValid] = useState(false);
    const fullUrl = `${window.location.origin}${location.pathname}${location.search}`;
    const productId = decodedData?.product_id;
    const employeeId = decodedData?.employee_id || null;
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const prompts = [
        "Tech Support",
        "Service",
        "Pricing & Value",
        "Sales Support",
        "Delivery Speed",
        "Trust & Security",
        "Product Quality",
        "Overall Satisfaction"
    ];
 
    const checkValidUrl = useCallback(async () => {
        try {
            const data = { link: fullUrl, product_id: productId };
            const response = await axios.post(`${API_BASE_URL}/feedback-links/check-url`, { data }, axiosConfig);
 
            if (response.status === 200) {
                setIsValid(true);
                setFeedbackLinkId(response.data.data.id);
            } else {
                setIsValid(false);
            }
        } catch (error) {
            setIsValid(false);
            console.warn(error?.message || "Error validating link.");
        }
    }, [fullUrl, productId]);
 
    useEffect(() => {
        checkValidUrl();
    }, [checkValidUrl]);
 
    const onSubmit = async (formData) => {
        try {
            const data = {
                ...formData,
                product_id: productId,
                employee_id: employeeId,
                feedback_link_id: feedbackLinkId,
            };
 
            const response = await axios.post(`${API_BASE_URL}/feedbacks`, { data }, axiosConfig);
 
            if (response?.status === 201) {
                toast.success(response?.data?.message);
                checkValidUrl();
                reset();
            } else {
                toast.error("Failed to submit feedback.");
            }
        } catch (error) {
            toast.error(error?.message || error.response?.data?.message || "An error occurred");
        }
    };
    const handleTagClick = (tag) => {
        setSelectedTags((prevTags) =>
            prevTags.includes(tag) ? prevTags.filter((t) => t !== tag) : [...prevTags, tag]
        );
    };
    useEffect(() => {
        setValue("tags", selectedTags);
    }, [selectedTags, setValue]);
 
    return (
        <>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-5 col-xl-6 my-5">
                        <div className="card mt-4">
                            <div className="card-header">
                                <h2 className="card-title">Feedback</h2>
                                <img src={decodedData?.image || "/assets/img/logo.png"} alt="Product" className="img-fluid" height="100" width="100" />
                            </div>
                            <div className="card-body">
                                {isValid ? (
                                    <div className="text-center">
                                        <h2 className="mb-3">Your opinion matters!</h2>
                                        <p className="text-body-secondary">
                                            Wroffy Technology Pvt Ltd requests you to share your experience for your recent order with us.
                                            Please take a moment to review your recent purchase.
                                        </p>
                                        <hr />
                                        <form onSubmit={handleSubmit(onSubmit)}>
                                            <div className="mb-3 text-center">
                                                <label className="form-label">Rate this product</label>
                                                <p>
                                                    <img src={decodedData?.image || "/assets/img/no-picture-available.jpg"} alt="Product" className="img-fluid" height="150" width="150" />
                                                </p>
                                                <Controller
                                                    name="rating"
                                                    control={control}
                                                    rules={{ required: "Rating is required" }}
                                                    render={({ field }) => (
                                                        <div>
                                                            {[...Array(5)].map((_, index) => {
                                                                const starValue = index + 1;
                                                                return (
                                                                    <FaStar
                                                                        key={index}
                                                                        size={25}
                                                                        className="mx-1"
                                                                        color={starValue <= field.value ? "#ffc107" : "#e4e5e9"}
                                                                        onClick={() => {
                                                                            field.onChange(starValue)
                                                                            setShowPrompt(starValue <= 3);
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                />
                                                {errors.rating && <div className="text-danger">{errors.rating.message}</div>}
                                            </div>
                                            {showPrompt && (
                                                prompts.map((prompt, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className={`btn btn-sm me-2 ${selectedTags.includes(prompt) ? "btn-success" : "btn-primary"}`}
                                                    onClick={() => handleTagClick(prompt)}
                                                >
                                                    {prompt}
                                                </button>                                                ))
                                            )}
                                            <div className="mb-3">
                                                <label className="form-label">Description</label>
                                                <textarea
                                                    className={`form-control ${errors.feedback ? "is-invalid" : ""}`}
                                                    {...register("feedback", {
                                                        required: "Feedback description is required",
                                                        maxLength: {
                                                            value: 255,
                                                            message: "Description cannot exceed 255 characters",
                                                        },
                                                    })}
                                                    rows="3"
                                                    placeholder="Enter feedback description"
                                                ></textarea>
                                                {errors.feedback && <div className="invalid-feedback">{errors.feedback.message}</div>}
                                            </div>
 
                                            <div className="text-center">
                                                <button type="submit" className="btn btn-primary">Submit Feedback</button>
                                            </div>
                                        </form>
                                    </div>
                                ) : (
                                    <h2 className="text-center text-info">Thanks for giving your valuable feedback.</h2>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </>
    );
};
 
export default CreateFeedback;